import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ListChecks, RefreshCw, Package, AlertCircle, Loader, Check, X, Edit3, Trash2, ChevronDown, ChevronUp, Info, Play, Pause, ArrowUpDown } from 'lucide-react';
import { pickingService } from '../../../../services/pickingService';
import { BulkUpdateModal, BulkUpdateChanges, BulkDeliveryMethodOption, cleanDeliveryMethodDescription } from './BulkUpdateModal';
import { deliveryMethodService } from '../../../../services/deliveryMethodService';
import { userProfileService } from '../../../../services/userProfileService';
import { PListDto, PListDetailsDto } from '../../../../types/picking';
import { UserProfileDto } from '../../../../types/userProfile';
import { BackButton } from '../../../common/BackButton';
import { StatusBadge } from '../../../common/StatusBadge';
import { useStatusOptions } from '../../../common/statusPresentation';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useWorkingPickingListsAutoRefresh } from '../../../../hooks/useWorkingPickingListsAutoRefresh';

// Import the reusable warehouse table system
import { WarehouseTable, createColumnFactory } from '../../../shared/table';
import type { ColumnDefinition, ExpandableRowConfig, TableDataRow, RowAction, SummaryCalculation } from '../../../shared/table';

// Extended type for table
interface PListTableRow extends TableDataRow {
  id: number;
  pListID: number;
  pListStatus: number;
  statusText: string;
  description1: string | null;
  description2: string | null;
  pListDate: string | null;
  deliveryDate: string | null;
  numOfOrderLines: number;
  numOfOrderLinesPicked: number | null;
  weight: number | null;
  volum: number | null;
  totalVolume: number | null;
  totalWeight: number | null;
  operatorID: string | null;
  operatorGroupID: string | null;
  priority: number;
  pListIsBatchYesNo: boolean | null;
  filterGroupID: string | null;
  lockedBy: string | null;
  lockStatus: number;
  // Shipping
  numOfPackages: number | null;
  numOfPallets: number | null;
  packZoneID: string | null;
  packageRemark: string | null;
  shippingLabelsInitiated: boolean | null;
  shippingLabelsPrinted: boolean | null;
  // Signatures
  signature1Location: string | null;
  signature1DateTime: string | null;
  signature2Location: string | null;
  signature2DateTime: string | null;
  // Batch/Configuration
  packagingTypeNo: number | null;
  pListNumOfPickZoneSplits: number | null;
  deliveryMethodNo: number | null;
  deliveryMethodDescription: string | null;
  clientID: string | null;
  sessionID: string | null;
  internalStatus: number | null;
  externalSystemID: number | null;
  allowAutoComplete: boolean | null;
  pListFinalizationLayout: number | null;
  approvePackageStatus: number | null;
  pListConfirmation: number | null;
  qtyDeviationControlTerminated: boolean | null;
  // Free fields
  pListFreeField1: string | null;
  pListFreeField2: string | null;
  pListFreeField3: string | null;
  pListFreeField4: string | null;
  pListFreeField5: string | null;
  pListFreeField6: string | null;
  pListFreeField7: string | null;
  pListFreeField8: string | null;
  pListFreeField9: string | null;
  pListFreeField10: string | null;
  // Work times
  startWorkDateTime: string | null;
  stopWorkDateTime: string | null;
  // Audit
  createdBy: string | null;
  created: string | null;
  updatedBy: string | null;
  lastUpdate: string | null;
  // Keep original for updates
  _original: PListDto;
}

// Create column factory for type-safe column definitions
const columnFactory = createColumnFactory<PListTableRow>();

// StatusType for LockStatus in StatusCodes table

interface EditableCellState {
  [pListId: number]: {
    operatorID?: string;
    priority?: number;
    lockStatus?: number;
    lockedBy?: string;
  };
}

interface WorkingPickingListsProps {
  onBack?: () => void;
}

const WorkingPickingLists: React.FC<WorkingPickingListsProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouse();
  const [pickingLists, setPickingLists] = useState<PListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  // Auto-refresh (for storskjerm-visning) — toggle av/på + intervall i sekunder
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [refreshIntervalSeconds, setRefreshIntervalSeconds] = useState(30);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);

  // Start-tilstand for auto-oppdatering styres av innstillingen
  // 'workingPickingLists.autoRefreshEnabled' (Profile -> System -> default=false).
  const { enabled: autoRefreshDefault, loaded: autoRefreshSettingLoaded } = useWorkingPickingListsAutoRefresh();
  const autoRefreshDefaultApplied = useRef(false);

  // Operators list for dropdown
  const [operators, setOperators] = useState<UserProfileDto[]>([]);
  const [loadingOperators, setLoadingOperators] = useState(false);

  // Kode-baserte, språk-bevisste opsjoner: pListStatus-filter (ST1) + inline lockStatus (ST2).
  const pListStatusOptions = useStatusOptions(1);
  const lockStatusOptions = useStatusOptions(2);

  // Expanded state and details
  const [listDetails, setListDetails] = useState<Map<number, PListDetailsDto>>(new Map());
  const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set());

  // Inline editing state
  const [editState, setEditState] = useState<EditableCellState>({});
  const [savingPLists, setSavingPLists] = useState<Set<number>>(new Set());
  const [savedPLists, setSavedPLists] = useState<Set<number>>(new Set());

  // Delete state
  const [deletingPListId, setDeletingPListId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pListToDelete, setPListToDelete] = useState<PListDto | null>(null);

  // Bulk update state (row selection + modal)
  const [selectedRowIds, setSelectedRowIds] = useState<(string | number)[]>([]);
  const [selectedData, setSelectedData] = useState<PListTableRow[]>([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [deliveryMethods, setDeliveryMethods] = useState<BulkDeliveryMethodOption[]>([]);

  // Refs to access current state in stable callbacks
  const editStateRef = useRef<EditableCellState>(editState);
  const pickingListsRef = useRef<PListDto[]>(pickingLists);
  const listDetailsRef = useRef<Map<number, PListDetailsDto>>(listDetails);

  // Keep refs in sync with state
  useEffect(() => {
    editStateRef.current = editState;
  }, [editState]);

  useEffect(() => {
    pickingListsRef.current = pickingLists;
  }, [pickingLists]);

  useEffect(() => {
    listDetailsRef.current = listDetails;
  }, [listDetails]);

  useEffect(() => {
    loadPickingLists();
    loadOperators();
    loadDeliveryMethods();
  }, []);

  // Leveringsmåter for bulk-modalens nedtrekk. getCurrent() gir rå Description; rydd for visning.
  const loadDeliveryMethods = async () => {
    try {
      const methods = await deliveryMethodService.getCurrent();
      setDeliveryMethods(
        methods
          .map(m => ({ deliveryMethodNo: m.deliveryMethodNo, description: cleanDeliveryMethodDescription(m.description) }))
          .sort((a, b) => a.deliveryMethodNo - b.deliveryMethodNo)
      );
    } catch (err) {
      console.error('Kunne ikke laste leveringsmåter:', err);
    }
  };

  const loadOperators = async () => {
    try {
      setLoadingOperators(true);
      const profiles = await userProfileService.getAllProfilesForCompany();
      setOperators(profiles);
    } catch (err) {
      console.error('Error loading operators:', err);
    } finally {
      setLoadingOperators(false);
    }
  };


  const loadPickingLists = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      // Hent plukklister med statuser: 10, 20, 40, 45, 50
      const data = await pickingService.getPickingListsByStatuses([10, 20, 40, 45, 50], currentWarehouse || undefined);
      setPickingLists(data);
      setLastRefreshTime(new Date());

      // Refresh cached line details for currently expanded rows
      const cachedIds = Array.from(listDetailsRef.current.keys());
      if (cachedIds.length > 0) {
        const refreshed = await Promise.all(
          cachedIds.map(async (id) => {
            try {
              const details = await pickingService.getPickingDetails(id);
              return [id, details] as const;
            } catch (err) {
              console.error(`Error refreshing details for picking list ${id}:`, err);
              return null;
            }
          })
        );
        setListDetails(prev => {
          const next = new Map(prev);
          refreshed.forEach(entry => {
            if (entry) next.set(entry[0], entry[1]);
          });
          return next;
        });
      }

      // Initialize edit state
      const newEditState: EditableCellState = {};
      data.forEach(pl => {
        newEditState[pl.pListID] = {
          operatorID: pl.operatorID || '',
          priority: pl.priority
        };
      });
      setEditState(newEditState);
    } catch (err) {
      // Ved stille auto-oppdatering: ikke blank ut tabellen med feilskjerm — behold forrige data, bare logg
      if (!silent) {
        setError(err instanceof Error ? err.message : t('manager.modules.newPickingLists.errors.loadFailed', {}, 'Feil ved lasting av plukklister'));
      }
      console.error('Error loading picking lists:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Hold en stabil referanse til siste loadPickingLists (funksjonen er ikke memoisert)
  const loadPickingListsRef = useRef(loadPickingLists);
  loadPickingListsRef.current = loadPickingLists;

  // Sett start-tilstanden for auto-oppdatering fra innstillingen, nøyaktig én gang når den
  // er lastet — uten å overstyre senere manuelle av/på-valg fra operatøren.
  useEffect(() => {
    if (autoRefreshSettingLoaded && !autoRefreshDefaultApplied.current) {
      autoRefreshDefaultApplied.current = true;
      setAutoRefreshEnabled(autoRefreshDefault);
    }
  }, [autoRefreshSettingLoaded, autoRefreshDefault]);

  // Auto-oppdatering: kjør stille refresh hvert X sekund når aktivert
  useEffect(() => {
    if (!autoRefreshEnabled) return;
    const intervalMs = Math.max(2, refreshIntervalSeconds) * 1000;
    const id = window.setInterval(() => {
      loadPickingListsRef.current(true);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [autoRefreshEnabled, refreshIntervalSeconds]);

  const loadPickingListDetails = async (pListId: number) => {
    try {
      setLoadingDetails(prev => new Set(prev).add(pListId));
      const details = await pickingService.getPickingDetails(pListId);
      setListDetails(prev => new Map(prev).set(pListId, details));
    } catch (err) {
      console.error(`Error loading details for picking list ${pListId}:`, err);
    } finally {
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(pListId);
        return newSet;
      });
    }
  };

  // Transform to table data
  const tableData: PListTableRow[] = useMemo(() => {
    return pickingLists.map(pl => ({
      id: pl.pListID,
      pListID: pl.pListID,
      pListStatus: pl.pListStatus,
      statusText: pl.statusText,
      description1: pl.description1 || null,
      description2: pl.description2 || null,
      pListDate: pl.pListDate || null,
      deliveryDate: pl.deliveryDate || null,
      numOfOrderLines: pl.numOfOrderLines || 0,
      numOfOrderLinesPicked: pl.numOfOrderLinesPicked ?? null,
      weight: pl.weight ?? null,
      volum: pl.volum ?? null,
      totalVolume: pl.totalVolume ?? null,
      totalWeight: pl.totalWeight ?? null,
      operatorID: pl.operatorID || null,
      operatorGroupID: pl.operatorGroupID || null,
      priority: pl.priority,
      pListIsBatchYesNo: pl.pListIsBatchYesNo ?? null,
      filterGroupID: pl.filterGroupID || null,
      lockedBy: pl.lockedBy || null,
      lockStatus: pl.lockStatus,
      // Shipping
      numOfPackages: pl.numOfPackages ?? null,
      numOfPallets: pl.numOfPallets ?? null,
      packZoneID: pl.packZoneID || null,
      packageRemark: pl.packageRemark || null,
      shippingLabelsInitiated: pl.shippingLabelsInitiated ?? null,
      shippingLabelsPrinted: pl.shippingLabelsPrinted ?? null,
      // Signatures
      signature1Location: pl.signature1Location || null,
      signature1DateTime: pl.signature1DateTime || null,
      signature2Location: pl.signature2Location || null,
      signature2DateTime: pl.signature2DateTime || null,
      // Batch/Configuration
      packagingTypeNo: pl.packagingTypeNo ?? null,
      pListNumOfPickZoneSplits: pl.pListNumOfPickZoneSplits ?? null,
      deliveryMethodNo: pl.deliveryMethodNo ?? null,
      deliveryMethodDescription: pl.deliveryMethodDescription ?? null,
      clientID: pl.clientID || null,
      sessionID: pl.sessionID || null,
      internalStatus: pl.internalStatus ?? null,
      externalSystemID: pl.externalSystemID ?? null,
      allowAutoComplete: pl.allowAutoComplete ?? null,
      pListFinalizationLayout: pl.pListFinalizationLayout ?? null,
      approvePackageStatus: pl.approvePackageStatus ?? null,
      pListConfirmation: pl.pListConfirmation ?? null,
      qtyDeviationControlTerminated: pl.qtyDeviationControlTerminated ?? null,
      // Free fields
      pListFreeField1: pl.pListFreeField1 || null,
      pListFreeField2: pl.pListFreeField2 || null,
      pListFreeField3: pl.pListFreeField3 || null,
      pListFreeField4: pl.pListFreeField4 || null,
      pListFreeField5: pl.pListFreeField5 || null,
      pListFreeField6: pl.pListFreeField6 || null,
      pListFreeField7: pl.pListFreeField7 || null,
      pListFreeField8: pl.pListFreeField8 || null,
      pListFreeField9: pl.pListFreeField9 || null,
      pListFreeField10: pl.pListFreeField10 || null,
      // Work times
      startWorkDateTime: pl.startWorkDateTime || null,
      stopWorkDateTime: pl.stopWorkDateTime || null,
      // Audit
      createdBy: pl.createdBy || null,
      created: pl.created || null,
      updatedBy: pl.updatedBy || null,
      lastUpdate: pl.lastUpdate || null,
      _original: pl
    }));
  }, [pickingLists]);

  // Row ID function
  const getRowId = useCallback((row: PListTableRow) => row.pListID, []);

  // Save inline edit
  const saveInlineEdit = useCallback(async (pListId: number, field: 'operatorID' | 'priority' | 'lockStatus' | 'lockedBy', newFieldValue?: string | number) => {
    const originalPickList = pickingListsRef.current.find(p => p.pListID === pListId);
    if (!originalPickList) return;

    // Get value either from parameter or from editState ref
    const editData = editStateRef.current[pListId];
    let newValue: string | number | undefined;

    if (newFieldValue !== undefined) {
      // Value passed directly (from onChange)
      newValue = newFieldValue;
    } else if (editData) {
      // Value from editState ref (from onBlur)
      newValue = editData[field];
    } else {
      return;
    }

    // Check if the specific field changed
    let oldValue: string | number;
    switch (field) {
      case 'priority': oldValue = originalPickList.priority; break;
      case 'lockStatus': oldValue = originalPickList.lockStatus; break;
      case 'lockedBy': oldValue = originalPickList.lockedBy || ''; break;
      default: oldValue = originalPickList.operatorID || ''; break;
    }

    if (newValue === oldValue) {
      return; // No change
    }

    try {
      setSavingPLists(prev => new Set(prev).add(pListId));

      // Build complete PList object with updated fields (backend requires full object)
      const pickListUpdate: PListDto = {
        ...originalPickList,
        operatorID: field === 'operatorID' ? (newValue as string || undefined) : originalPickList.operatorID,
        priority: field === 'priority' ? (newValue as number) : originalPickList.priority,
        lockStatus: field === 'lockStatus' ? (newValue as number) : originalPickList.lockStatus,
        lockedBy: field === 'lockedBy' ? (newValue as string || undefined) : originalPickList.lockedBy
      };

      const saveResult = await pickingService.updatePickingList(pListId, pickListUpdate);

      // Oppdater lokal state + frisk LastUpdate-token (E: så managerens neste redigering av samme
      // rad ikke avvises som "endret av andre").
      const refreshedLastUpdate = saveResult?.lastUpdate ?? pickListUpdate.lastUpdate;
      setPickingLists(prev => prev.map(p =>
        p.pListID === pListId ? { ...p, ...pickListUpdate, lastUpdate: refreshedLastUpdate } : p
      ));

      // Update editState to reflect saved value
      setEditState(prev => ({
        ...prev,
        [pListId]: {
          ...prev[pListId],
          operatorID: pickListUpdate.operatorID || '',
          priority: pickListUpdate.priority,
          lockStatus: pickListUpdate.lockStatus,
          lockedBy: pickListUpdate.lockedBy || ''
        }
      }));

      // Show saved indicator
      setSavedPLists(prev => new Set(prev).add(pListId));
      setTimeout(() => {
        setSavedPLists(prev => {
          const newSet = new Set(prev);
          newSet.delete(pListId);
          return newSet;
        });
      }, 2000);

    } catch (err) {
      console.error('Error saving:', err);
      alert(`${t('manager.modules.newPickingLists.errors.saveFailed', {}, 'Feil ved lagring')}: ${err instanceof Error ? err.message : t('manager.modules.newPickingLists.errors.unknownError', {}, 'Ukjent feil')}`);
      // E: ved samtidighetskonflikt (eller annen avvist lagring) — last listen på nytt så manageren
      // får ferske verdier og friske LastUpdate-token før neste forsøk.
      loadPickingLists();
    } finally {
      setSavingPLists(prev => {
        const newSet = new Set(prev);
        newSet.delete(pListId);
        return newSet;
      });
    }
  }, [t]);

  // Inline editable OperatorID cell (dropdown) - uses key to force re-render after save
  const renderOperatorCell = useCallback((value: string | null, row: PListTableRow) => {
    const pListId = row.pListID;
    // Case-insensitive match: OperatorID i DB kan ha annen casing enn UserID i Profile-tabellen
    const matchedOperator = value
      ? operators.find(op => op.userID.toLowerCase() === value.toLowerCase())
      : null;
    const resolvedValue = matchedOperator?.userID || value || '';

    const stopAllPropagation = (e: React.SyntheticEvent) => {
      e.stopPropagation();
    };

    return (
      <div
        className="flex items-center space-x-1"
        onClick={stopAllPropagation}
        onMouseDown={stopAllPropagation}
      >
        <select
          key={`operator-select-${pListId}-${resolvedValue}-${operators.length}`}
          defaultValue={resolvedValue}
          onChange={(e) => {
            e.stopPropagation();
            const newValue = e.target.value;
            setEditState(prev => ({
              ...prev,
              [pListId]: { ...prev[pListId], operatorID: newValue }
            }));
            // Save immediately on change, passing value directly
            saveInlineEdit(pListId, 'operatorID', newValue);
          }}
          onClick={stopAllPropagation}
          onMouseDown={stopAllPropagation}
          onFocus={stopAllPropagation}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white"
        >
          <option value="">-</option>
          {operators.map(op => (
            <option key={op.userID} value={op.userID}>
              {op.profileName || op.userID}
            </option>
          ))}
        </select>
      </div>
    );
  }, [operators, saveInlineEdit]);

  // Inline editable Priority cell (number input) - uses uncontrolled input like NewPickingLists
  const renderPriorityCell = useCallback((value: number, row: PListTableRow) => {
    const pListId = row.pListID;

    const stopAllPropagation = (e: React.SyntheticEvent) => {
      e.stopPropagation();
    };

    return (
      <div
        className="flex items-center space-x-1"
        onClick={stopAllPropagation}
        onMouseDown={stopAllPropagation}
      >
        <input
          key={`priority-input-${pListId}-${value}`}
          type="number"
          defaultValue={value}
          min={0}
          max={999}
          onChange={(e) => {
            e.stopPropagation();
            const newValue = parseInt(e.target.value) || 0;
            setEditState(prev => ({
              ...prev,
              [pListId]: { ...prev[pListId], priority: newValue }
            }));
          }}
          onBlur={() => saveInlineEdit(pListId, 'priority')}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              e.preventDefault();
              saveInlineEdit(pListId, 'priority');
              (e.target as HTMLInputElement).blur();
            }
          }}
          onClick={stopAllPropagation}
          onMouseDown={stopAllPropagation}
          onFocus={stopAllPropagation}
          className="w-16 px-2 py-1 text-xs text-right border border-gray-300 rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white"
        />
      </div>
    );
  }, [saveInlineEdit]);

  // Inline editable LockStatus cell (dropdown) - uses lockStatusOptions from StatusCodes
  const renderLockStatusCell = useCallback((value: number, row: PListTableRow) => {
    const pListId = row.pListID;

    const stopAllPropagation = (e: React.SyntheticEvent) => {
      e.stopPropagation();
    };

    return (
      <div
        className="flex items-center space-x-1"
        onClick={stopAllPropagation}
        onMouseDown={stopAllPropagation}
      >
        <select
          key={`lockstatus-select-${pListId}-${value}-${lockStatusOptions.length}`}
          defaultValue={value}
          onChange={(e) => {
            e.stopPropagation();
            const newValue = parseInt(e.target.value) || 0;
            setEditState(prev => ({
              ...prev,
              [pListId]: { ...prev[pListId], lockStatus: newValue }
            }));
            saveInlineEdit(pListId, 'lockStatus', newValue);
          }}
          onClick={stopAllPropagation}
          onMouseDown={stopAllPropagation}
          onFocus={stopAllPropagation}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white"
        >
          {lockStatusOptions.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }, [lockStatusOptions, saveInlineEdit]);

  // Inline editable LockedBy cell (dropdown) - uses operators list
  const renderLockedByCell = useCallback((value: string | null, row: PListTableRow) => {
    const pListId = row.pListID;
    // Case-insensitive match: LockedBy i DB kan ha annen casing enn UserID i Profile-tabellen
    const matchedOperator = value
      ? operators.find(op => op.userID.toLowerCase() === value.toLowerCase())
      : null;
    const resolvedValue = matchedOperator?.userID || value || '';

    const stopAllPropagation = (e: React.SyntheticEvent) => {
      e.stopPropagation();
    };

    return (
      <div
        className="flex items-center space-x-1"
        onClick={stopAllPropagation}
        onMouseDown={stopAllPropagation}
      >
        <select
          key={`lockedby-select-${pListId}-${resolvedValue}-${operators.length}`}
          defaultValue={resolvedValue}
          onChange={(e) => {
            e.stopPropagation();
            const newValue = e.target.value;
            setEditState(prev => ({
              ...prev,
              [pListId]: { ...prev[pListId], lockedBy: newValue }
            }));
            saveInlineEdit(pListId, 'lockedBy', newValue);
          }}
          onClick={stopAllPropagation}
          onMouseDown={stopAllPropagation}
          onFocus={stopAllPropagation}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white"
        >
          <option value="">-</option>
          {operators.map(op => (
            <option key={op.userID} value={op.userID}>
              {op.profileName || op.userID}
            </option>
          ))}
        </select>
      </div>
    );
  }, [operators, saveInlineEdit]);

  // Row actions (Handling column)
  const rowActions: RowAction<PListTableRow>[] = useMemo(() => [
    {
      id: 'delete-plist',
      label: t('manager.modules.newPickingLists.actions.deletePickingList', {}, 'Slett plukkliste'),
      icon: Trash2,
      variant: 'danger' as const,
      disabled: (row: PListTableRow) =>
        row.pListStatus > 20 ||
        !!row.lockedBy ||
        (row.numOfOrderLinesPicked ?? 0) > 0,
      onClick: (row: PListTableRow) => {
        setPListToDelete(row._original);
        setShowDeleteConfirm(true);
      }
    }
  ], [t]);


  // Define columns based on legacy PListOptimized.dfm - all database fields available
  const columns: ColumnDefinition<PListTableRow>[] = useMemo(() => [
    // === HOVEDFELTER (synlige som standard) ===
    columnFactory.createNumberColumn('pListID', 'pListID', t('manager.modules.newPickingLists.table.pListNo', {}, 'Plukklistenr.'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-'
    }),
    columnFactory.createTextColumn('pListStatus', 'pListStatus', t('common.fields.status', {}, 'Status'), {
      width: 110,
      sortable: true,
      filterType: 'dropdown',
      filterOptions: pListStatusOptions.map(o => ({ value: String(o.value), label: o.label })),
      renderer: (_value, row) => <StatusBadge value={row.pListStatus} statusType={1} />
    }),
    // Editable: LockedBy — filtrer på profilnavn (vist), lagret/matchet verdi = UserID
    columnFactory.createTextColumn('lockedBy', 'lockedBy', t('manager.modules.newPickingLists.table.lockedByCorrect', {}, 'Låst av'), {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      filterOptions: operators.map(op => ({ value: op.userID, label: op.profileName || op.userID })),
      renderer: renderLockedByCell
    }),
    // Editable: LockStatus — filtrer på statustekst (vist), verdi = statuskode
    columnFactory.createNumberColumn('lockStatus', 'lockStatus', t('manager.modules.newPickingLists.table.systemStatus', {}, 'Systemstatus'), {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      filterOptions: lockStatusOptions.map(o => ({ value: String(o.value), label: o.label })),
      renderer: renderLockStatusCell
    }),
    columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
      width: 200,
      sortable: true,
      filterType: 'dropdown',
      enableDropdownFilter: true, // distinct-verdier fra dataene (ikke oppslag)
      formatter: (value) => value || '-'
    }),
    columnFactory.createDateColumn('pListDate', 'pListDate', t('manager.modules.newPickingLists.table.createdDate', {}, 'Opprettet dato'), {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createDateColumn('deliveryDate', 'deliveryDate', t('manager.modules.newPickingLists.table.deliveryDate', {}, 'Leveringsdato'), {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('numOfOrderLines', 'numOfOrderLines', t('manager.modules.newPickingLists.table.numLines', {}, 'Ant. linjer'), {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createNumberColumn('numOfOrderLinesPicked', 'numOfOrderLinesPicked', t('manager.modules.newPickingLists.table.numPicked', {}, 'Ant. plukket'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-'
    }),
    columnFactory.createNumberColumn('weight', 'weight', t('warehouse.general.weight', {}, 'Vekt'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? `${value.toFixed(2)} kg` : '-'
    }),
    // Editable: OperatorID — filtrer på profilnavn (vist), lagret/matchet verdi = UserID
    columnFactory.createTextColumn('operatorID', 'operatorID', t('manager.modules.newPickingLists.table.operator', {}, 'Operatør'), {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      filterOptions: operators.map(op => ({ value: op.userID, label: op.profileName || op.userID })),
      renderer: renderOperatorCell
    }),
    // Editable: Priority
    columnFactory.createNumberColumn('priority', 'priority', t('manager.modules.newPickingLists.table.priority', {}, 'Prioritet'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      renderer: renderPriorityCell
    }),

    // === BESKRIVELSE (skjult som standard) ===
    columnFactory.createTextColumn('description2', 'description2', t('manager.modules.newPickingLists.table.description2', {}, 'Beskrivelse 2'), {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === VOLUM OG VEKT (skjult som standard) ===
    columnFactory.createNumberColumn('volum', 'volum', t('manager.modules.newPickingLists.table.volumeUnit', {}, 'Volum (enhet)'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toFixed(2) : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('totalVolume', 'totalVolume', t('manager.modules.newPickingLists.table.totalVolume', {}, 'Total volum'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toFixed(2) : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('totalWeight', 'totalWeight', t('manager.modules.newPickingLists.table.totalWeight', {}, 'Total vekt'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? `${value.toFixed(2)} kg` : '-',
      visible: false
    }),

    // === OPERATØR (skjult som standard) ===
    columnFactory.createTextColumn('operatorGroupID', 'operatorGroupID', t('manager.modules.newPickingLists.table.operatorGroup', {}, 'Operatørgruppe'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === BATCH/FILTER (skjult som standard) ===
    columnFactory.createTextColumn('pListIsBatchYesNo', 'pListIsBatchYesNo', t('manager.modules.newPickingLists.table.batchPick', {}, 'Samplukk'), {
      width: 90,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.newPickingLists.common.yes', {}, 'Ja') : value === false ? t('manager.modules.newPickingLists.common.no', {}, 'Nei') : '-',
      visible: false
    }),
    columnFactory.createTextColumn('filterGroupID', 'filterGroupID', t('manager.modules.newPickingLists.table.filterGroup', {}, 'Filtergruppe'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === PAKKING/SHIPPING (skjult som standard) ===
    columnFactory.createNumberColumn('numOfPackages', 'numOfPackages', t('manager.modules.newPickingLists.table.numPackages', {}, 'Ant. kolli'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('numOfPallets', 'numOfPallets', t('manager.modules.newPickingLists.table.numPallets', {}, 'Ant. paller'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toFixed(1) : '-',
      visible: false
    }),
    columnFactory.createTextColumn('packZoneID', 'packZoneID', t('manager.modules.newPickingLists.table.loadZone', {}, 'Lastesone'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('packageRemark', 'packageRemark', t('manager.modules.newPickingLists.table.packageRemark', {}, 'Pakkekommentar'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('shippingLabelsInitiated', 'shippingLabelsInitiated', t('manager.modules.newPickingLists.table.shippingLabelsInitiated', {}, 'Fraktmerker init.'), {
      width: 110,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.newPickingLists.common.yes', {}, 'Ja') : value === false ? t('manager.modules.newPickingLists.common.no', {}, 'Nei') : '-',
      visible: false
    }),
    columnFactory.createTextColumn('shippingLabelsPrinted', 'shippingLabelsPrinted', t('manager.modules.newPickingLists.table.shippingLabelsPrinted', {}, 'Fraktmerker skrevet'), {
      width: 120,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.newPickingLists.common.yes', {}, 'Ja') : value === false ? t('manager.modules.newPickingLists.common.no', {}, 'Nei') : '-',
      visible: false
    }),

    // === SIGNATURER (skjult som standard) ===
    columnFactory.createTextColumn('signature1Location', 'signature1Location', t('manager.modules.newPickingLists.table.signature1Location', {}, 'Signatur 1 sted'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createDateColumn('signature1DateTime', 'signature1DateTime', t('manager.modules.newPickingLists.table.signature1Time', {}, 'Signatur 1 tid'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('signature2Location', 'signature2Location', t('manager.modules.newPickingLists.table.signature2Location', {}, 'Signatur 2 sted'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createDateColumn('signature2DateTime', 'signature2DateTime', t('manager.modules.newPickingLists.table.signature2Time', {}, 'Signatur 2 tid'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // === ARBEIDSTIDER (skjult som standard) ===
    columnFactory.createDateColumn('startWorkDateTime', 'startWorkDateTime', t('manager.modules.newPickingLists.table.workStarted', {}, 'Arbeid startet'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('stopWorkDateTime', 'stopWorkDateTime', t('manager.modules.newPickingLists.table.workStopped', {}, 'Arbeid stoppet'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // === KONFIGURASJON (skjult som standard) ===
    columnFactory.createNumberColumn('packagingTypeNo', 'packagingTypeNo', t('manager.modules.newPickingLists.table.packagingType', {}, 'Emballasjetype'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('pListNumOfPickZoneSplits', 'pListNumOfPickZoneSplits', t('manager.modules.newPickingLists.table.pickZoneSplits', {}, 'Plukksonesplitter'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    // Leveringsmåte: viser DeliveryMethods.Description (slått opp via DeliveryMethodNo i backend),
    // med fallback til selve nummeret hvis leveringsmåten mangler beskrivelse. Synlig som standard —
    // erstatter den fjernede «Transportør»-kolonnen (CarrierName var upålitelig/utdatert).
    columnFactory.createTextColumn('deliveryMethodNo', 'deliveryMethodDescription', t('manager.modules.newPickingLists.table.deliveryMethod', {}, 'Leveringsmetode'), {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value, row) => value || (row.deliveryMethodNo !== null ? row.deliveryMethodNo!.toString() : '-')
    }),
    columnFactory.createTextColumn('allowAutoComplete', 'allowAutoComplete', t('manager.modules.newPickingLists.table.autoComplete', {}, 'Autofullføring'), {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.newPickingLists.common.yes', {}, 'Ja') : value === false ? t('manager.modules.newPickingLists.common.no', {}, 'Nei') : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('pListFinalizationLayout', 'pListFinalizationLayout', t('manager.modules.newPickingLists.table.finalizationLayout', {}, 'Ferdigstillelseslayout'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('approvePackageStatus', 'approvePackageStatus', t('manager.modules.newPickingLists.table.approvePackageStatus', {}, 'Godkjenn pakkestatus'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('pListConfirmation', 'pListConfirmation', t('manager.modules.newPickingLists.table.confirmation', {}, 'Bekreftelse'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createTextColumn('qtyDeviationControlTerminated', 'qtyDeviationControlTerminated', t('manager.modules.newPickingLists.table.deviationControlTerminated', {}, 'Avvikskontroll avsl.'), {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.newPickingLists.common.yes', {}, 'Ja') : value === false ? t('manager.modules.newPickingLists.common.no', {}, 'Nei') : '-',
      visible: false
    }),

    // === KLIENT/SESJON (skjult som standard) ===
    columnFactory.createTextColumn('clientID', 'clientID', t('manager.modules.newPickingLists.table.clientId', {}, 'Klient-ID'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('sessionID', 'sessionID', t('manager.modules.newPickingLists.table.sessionId', {}, 'Sesjons-ID'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createNumberColumn('internalStatus', 'internalStatus', t('manager.modules.newPickingLists.table.internalStatus', {}, 'Intern status'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),
    columnFactory.createNumberColumn('externalSystemID', 'externalSystemID', t('manager.modules.newPickingLists.table.externalSystem', {}, 'Eksternt system'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-',
      visible: false
    }),

    // === FRIE FELT (skjult som standard) ===
    columnFactory.createTextColumn('pListFreeField1', 'pListFreeField1', t('manager.modules.newPickingLists.table.freeField', { '0': 1 }, 'Fritt felt 1'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField2', 'pListFreeField2', t('manager.modules.newPickingLists.table.freeField', { '0': 2 }, 'Fritt felt 2'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField3', 'pListFreeField3', t('manager.modules.newPickingLists.table.freeField', { '0': 3 }, 'Fritt felt 3'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField4', 'pListFreeField4', t('manager.modules.newPickingLists.table.freeField', { '0': 4 }, 'Fritt felt 4'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField5', 'pListFreeField5', t('manager.modules.newPickingLists.table.freeField', { '0': 5 }, 'Fritt felt 5'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField6', 'pListFreeField6', t('manager.modules.newPickingLists.table.freeField', { '0': 6 }, 'Fritt felt 6'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField7', 'pListFreeField7', t('manager.modules.newPickingLists.table.freeField', { '0': 7 }, 'Fritt felt 7'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField8', 'pListFreeField8', t('manager.modules.newPickingLists.table.freeField', { '0': 8 }, 'Fritt felt 8'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField9', 'pListFreeField9', t('manager.modules.newPickingLists.table.freeField', { '0': 9 }, 'Fritt felt 9'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createTextColumn('pListFreeField10', 'pListFreeField10', t('manager.modules.newPickingLists.table.freeField', { '0': 10 }, 'Fritt felt 10'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === AUDIT (skjult som standard) ===
    columnFactory.createTextColumn('createdBy', 'createdBy', t('manager.modules.newPickingLists.table.createdBy', {}, 'Opprettet av'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createDateColumn('created', 'created', t('manager.modules.newPickingLists.table.created', {}, 'Opprettet'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('updatedBy', 'updatedBy', t('manager.modules.newPickingLists.table.updatedBy', {}, 'Oppdatert av'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    columnFactory.createDateColumn('lastUpdate', 'lastUpdate', t('manager.modules.newPickingLists.table.lastUpdate', {}, 'Sist oppdatert'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

  ], [operators, pListStatusOptions, lockStatusOptions, renderOperatorCell, renderPriorityCell, renderLockStatusCell, renderLockedByCell, t]);

  // Summeringsrad i bunnen: totalt antall plukklinjer og antall plukket på tvers av
  // synlige (filtrerte) rader. Beregnes av WarehouseTable over processedData.
  const summaryCalculations: SummaryCalculation<PListTableRow>[] = useMemo(() => [
    // Antall plukklister (filter-følsomt) — vises under Plukklistenr-kolonnen.
    { column: 'pListID' as keyof PListTableRow, type: 'count' as const },
    { column: 'numOfOrderLines' as keyof PListTableRow, type: 'sum' as const },
    { column: 'numOfOrderLinesPicked' as keyof PListTableRow, type: 'sum' as const }
  ], []);

  // Selection change handler (controlled selection for bulk actions)
  const handleSelectionChange = useCallback((rowIds: (string | number)[], rows: PListTableRow[]) => {
    setSelectedRowIds(rowIds);
    setSelectedData(rows);
  }, []);

  // Bulk update — loops the existing per-list endpoints for each selected pick. Only fields the
  // manager enabled in the modal are applied. Prioritet/leveringsdato → PList (updatePickingList);
  // leveringsmåte → PListOrders (updatePickingListDeliveryMethod) for alle ordrer på plukket.
  const handleBulkUpdate = useCallback(async (changes: BulkUpdateChanges) => {
    if (selectedData.length === 0) return;

    const updatesPList = changes.priority !== undefined || changes.deliveryDate !== undefined;
    const updateDeliveryMethod = changes.deliveryMethodNo !== undefined;
    if (!updatesPList && !updateDeliveryMethod) return;

    setBulkSaving(true);
    setBulkProgress({ done: 0, total: selectedData.length });

    const succeeded: number[] = [];
    const failed: number[] = [];

    // Sequential so progress is meaningful and the backend isn't hit with a large parallel burst
    for (let i = 0; i < selectedData.length; i++) {
      const original = selectedData[i]._original;
      try {
        if (updatesPList) {
          const payload: any = { ...original };
          if (changes.priority !== undefined) payload.priority = changes.priority;
          if (changes.deliveryDate !== undefined) payload.deliveryDate = changes.deliveryDate;
          await pickingService.updatePickingList(original.pListID, payload);
        }
        if (updateDeliveryMethod) {
          await pickingService.updatePickingListDeliveryMethod(original.pListID, changes.deliveryMethodNo!);
        }
        succeeded.push(original.pListID);
      } catch (err) {
        console.error(`Error bulk-updating PList ${original.pListID}:`, err);
        failed.push(original.pListID);
      }
      setBulkProgress({ done: i + 1, total: selectedData.length });
    }

    // Patch local state for successful updates (consistent with inline edit — no full reload)
    const succeededSet = new Set(succeeded);
    const dmDescription = updateDeliveryMethod
      ? (deliveryMethods.find(d => d.deliveryMethodNo === changes.deliveryMethodNo)?.description ?? null)
      : null;
    setPickingLists(prev => prev.map(p => {
      if (!succeededSet.has(p.pListID)) return p;
      const patched: PListDto = { ...p };
      if (changes.priority !== undefined) patched.priority = changes.priority;
      if (changes.deliveryDate !== undefined) patched.deliveryDate = changes.deliveryDate;
      if (updateDeliveryMethod) {
        patched.deliveryMethodNo = changes.deliveryMethodNo!;
        patched.deliveryMethodDescription = dmDescription;
      }
      return patched;
    }));
    if (changes.priority !== undefined) {
      const newPriority = changes.priority;
      setEditState(prev => {
        const next = { ...prev };
        succeeded.forEach(id => {
          if (next[id]) next[id] = { ...next[id], priority: newPriority };
        });
        return next;
      });
    }

    setBulkSaving(false);
    setBulkProgress(null);
    setShowBulkModal(false);
    setSelectedRowIds([]);
    setSelectedData([]);

    if (failed.length > 0) {
      alert(t('manager.modules.newPickingLists.bulkUpdate.partialFailure', { '0': failed.length, '1': failed.join(', ') }, `Kunne ikke oppdatere ${failed.length} plukkliste(r): ${failed.join(', ')}`));
    }
  }, [selectedData, deliveryMethods, t]);

  // Delete handlers
  const handleConfirmDelete = async () => {
    if (!pListToDelete) return;

    try {
      setDeletingPListId(pListToDelete.pListID);
      setShowDeleteConfirm(false);

      const result = await pickingService.deletePickingList(pListToDelete.pListID);

      if (result.success) {
        setPickingLists(prev => prev.filter(p => p.pListID !== pListToDelete.pListID));
        alert(result.message);
      }
    } catch (err) {
      console.error('Error deleting PList:', err);
      alert(`${t('manager.modules.newPickingLists.delete.deleteFailed', {}, 'Feil ved sletting')}: ${err instanceof Error ? err.message : t('manager.modules.newPickingLists.errors.unknownError', {}, 'Ukjent feil')}`);
    } finally {
      setDeletingPListId(null);
      setPListToDelete(null);
    }
  };

  // Detail renderer for expandable rows
  const renderPListLines = useCallback((pList: PListTableRow, details: PListDetailsDto | undefined) => {
    const isLoadingDetails = loadingDetails.has(pList.pListID);

    if (isLoadingDetails) {
      return (
        <div className="flex items-center justify-center py-8 bg-slate-50">
          <Loader className="animate-spin mr-2" size={20} />
          <span className="text-gray-600">{t('manager.modules.newPickingLists.lines.loadingLines', {}, 'Laster linjer...')}</span>
        </div>
      );
    }

    if (!details) {
      return (
        <div className="text-center py-8 text-gray-500 bg-slate-50">
          {t('manager.modules.newPickingLists.lines.couldNotLoadLines', {}, 'Kunne ikke laste linjer')}
        </div>
      );
    }

    return (
      <div className="bg-slate-50 p-3 border-t border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
          <Package size={16} />
          <span>{t('manager.modules.newPickingLists.lines.linesForList', { '0': pList.pListID }, `Plukkliste-linjer for ${pList.pListID}`)}</span>
          <span className="text-xs text-gray-500">
            ({t('manager.modules.newPickingLists.lines.completedSummary', { '0': details.completedLines, '1': details.totalLines, '2': details.completionPercentage.toFixed(0) }, `${details.completedLines} av ${details.totalLines} fullført - ${details.completionPercentage.toFixed(0)}%`)})
          </span>
        </h4>

        {details.lines.length > 0 ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100">
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.lineNo', {}, 'Linjenr')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.orderNo', {}, 'Ordrenr')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.erpOrderNo', {}, 'ERP ordrenr')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.orderLine', {}, 'Ordre linje')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.erpLine', {}, 'ERP linje')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.itemNo', {}, 'Varenr')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">{t('common.fields.quantity', {}, 'Antall')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">{t('warehouse.general.reserved', {}, 'Reservert')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.picked', {}, 'Plukket')}</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.remaining', {}, 'Gjenstående')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('warehouse.general.bin', {}, 'Lokasjon')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('warehouse.general.warehouse', {}, 'Lager')}</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">{t('common.fields.status', {}, 'Status')}</th>
                <th className="text-center py-2 px-3 font-medium text-slate-500">{t('manager.modules.newPickingLists.lines.completed', {}, 'Fullført')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {details.lines.map((line) => (
                <tr key={line.id} className="hover:bg-slate-100 transition-colors">
                  <td className="py-2 px-3 text-slate-900 font-medium">{line.pListLineNo}</td>
                  <td className="py-2 px-3 text-slate-900">{line.orderNo}</td>
                  <td className="py-2 px-3 text-slate-900">{line.orderNoERP || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.lineNo}</td>
                  <td className="py-2 px-3 text-slate-900">{line.lineNoERP ?? '-'}</td>
                  <td className="py-2 px-3 text-slate-900 font-mono">{line.itemNo || '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qty.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qtyReserved.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qtyPicked.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900 text-right font-medium">{line.remainingQty.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-900">{line.binCode || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.warehouseID || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.lineStatus}</td>
                  <td className="py-2 px-3 text-center">
                    {line.isCompleted ? (
                      <Check className="text-green-600 mx-auto" size={16} />
                    ) : (
                      <X className="text-gray-400 mx-auto" size={16} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {t('manager.modules.newPickingLists.lines.noLinesFound', {}, 'Ingen linjer funnet for denne plukklisten')}
          </div>
        )}
      </div>
    );
  }, [loadingDetails, t]);

  // Expandable configuration
  const expandableConfig: ExpandableRowConfig<PListTableRow, PListDetailsDto | undefined> = useMemo(() => ({
    getDetailData: (pList) => listDetails.get(pList.pListID),
    renderDetail: renderPListLines,
    expandOnRowClick: false,
    onExpand: async (pList) => {
      if (!listDetails.has(pList.pListID)) {
        await loadPickingListDetails(pList.pListID);
      }
    }
  }), [listDetails, renderPListLines]);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => loadPickingLists(),
      variant: 'secondary' as const
    }
  ], [t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>{t('manager.modules.newPickingLists.loadingPickingLists', {}, 'Laster plukklister...')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
        <h3 className="text-red-800 font-medium">{t('manager.modules.newPickingLists.errors.loadFailed', {}, 'Feil ved lasting av plukklister')}</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={() => loadPickingLists()}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>{t('manager.modules.newPickingLists.tryAgain', {}, 'Prøv igjen')}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <ListChecks size={24} />
            <span>{t('manager.modules.newPickingLists.working.title', {}, 'Plukklister under arbeid')}</span>
          </h1>
          <span className="text-sm text-gray-500">
            {t('manager.modules.newPickingLists.listCount', { '0': tableData.length }, `${tableData.length} plukklister`)}
          </span>

          {/* Auto-oppdatering for storskjerm-visning */}
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => { autoRefreshDefaultApplied.current = true; setAutoRefreshEnabled(prev => !prev); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                autoRefreshEnabled
                  ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
              aria-pressed={autoRefreshEnabled}
              title={t('manager.modules.newPickingLists.working.autoRefreshTooltip', {}, 'Oppdater listen automatisk med jevne mellomrom (for storskjerm)')}
            >
              {autoRefreshEnabled ? <Pause size={14} /> : <Play size={14} />}
              <span>
                {autoRefreshEnabled
                  ? t('manager.modules.newPickingLists.working.autoRefreshOn', {}, 'Auto-oppdatering på')
                  : t('manager.modules.newPickingLists.working.autoRefreshOff', {}, 'Auto-oppdatering av')}
              </span>
              {autoRefreshEnabled && (
                <RefreshCw size={12} className="animate-spin" />
              )}
            </button>

            <div className="flex items-center gap-1 text-sm text-gray-600">
              <input
                type="number"
                min={2}
                max={3600}
                value={refreshIntervalSeconds}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setRefreshIntervalSeconds(Number.isNaN(v) ? 30 : Math.min(3600, Math.max(2, v)));
                }}
                className="w-16 px-2 py-1.5 text-sm text-right border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white"
                aria-label={t('manager.modules.newPickingLists.working.autoRefreshIntervalLabel', {}, 'Oppdateringsintervall i sekunder')}
              />
              <span>{t('manager.modules.newPickingLists.working.secondsSuffix', {}, 'sek')}</span>
            </div>

            {autoRefreshEnabled && lastRefreshTime && (
              <span className="text-xs text-gray-400">
                {t('manager.modules.newPickingLists.working.lastUpdated', { '0': lastRefreshTime.toLocaleTimeString('nb-NO') }, `Sist oppdatert ${lastRefreshTime.toLocaleTimeString('nb-NO')}`)}
              </span>
            )}
          </div>
        </div>
        {/* Info toggle */}
        <button
          type="button"
          onClick={() => setShowInfo(prev => !prev)}
          className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 focus:outline-none"
          aria-expanded={showInfo}
        >
          <Info size={14} />
          <span>{t('manager.modules.newPickingLists.working.infoToggleLabel', {}, 'Hva er Plukklister under arbeid?')}</span>
          {showInfo ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {showInfo && (
          <div className="mt-2 mb-2 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-800">
            <div className="mb-3">
              <h3 className="font-bold text-gray-900 mb-1">{t('manager.modules.newPickingLists.info.purposeHeading', {}, 'Hensikt')}</h3>
              <p>
                {t('manager.modules.newPickingLists.working.purposeBody', {}, 'Plukklister under arbeid viser alle plukklister som er godkjent eller pågående — fra de er klare for plukk til de er ferdig pakket men ikke avsluttet. Herfra følger en leder med på fremdrift, kan tildele/endre operatør, justere prioritet og prioritere låsing eller frigi plukklister som henger.')}
              </p>
            </div>

            <div className="mb-3">
              <h3 className="font-bold text-gray-900 mb-1">{t('manager.modules.newPickingLists.info.criteriaHeading', {}, 'Utvalgskriterier')}</h3>
              <p className="mb-1">{t('manager.modules.newPickingLists.working.criteriaIntro', {}, 'En plukkliste vises i listen når den har én av disse statusene:')}</p>
              <ol className="list-decimal pl-5 space-y-0.5">
                <li>
                  <code className="bg-blue-100 px-1 rounded text-xs">PListStatus = 10</code>{' '}
                  (<strong>{t('manager.modules.newPickingLists.statusLabels.approved', {}, 'Godkjent')}</strong>{t('manager.modules.newPickingLists.working.criteriaStatus10Suffix', {}, ' — klar for plukk, ikke startet')})
                </li>
                <li>
                  <code className="bg-blue-100 px-1 rounded text-xs">PListStatus = 20</code>{' '}
                  (<strong>{t('manager.modules.newPickingLists.statusLabels.inProgress', {}, 'Under arbeid')}</strong>{t('manager.modules.newPickingLists.working.criteriaStatus20Suffix', {}, ' — operatør har startet plukking')})
                </li>
                <li>
                  <code className="bg-blue-100 px-1 rounded text-xs">PListStatus = 40</code>{' '}
                  (<strong>{t('manager.modules.newPickingLists.statusLabels.picked', {}, 'Plukket')}</strong>{t('manager.modules.newPickingLists.working.criteriaStatus40Suffix', {}, ' — plukk fullført, venter på neste steg')})
                </li>
                <li>
                  <code className="bg-blue-100 px-1 rounded text-xs">PListStatus = 45</code>{' '}
                  (<strong>{t('manager.modules.newPickingLists.statusLabels.toPacking', {}, 'Til pakking')}</strong>{t('manager.modules.newPickingLists.working.criteriaStatus45Suffix', {}, ' — sendt til pakkesonen')})
                </li>
                <li>
                  <code className="bg-blue-100 px-1 rounded text-xs">PListStatus = 50</code>{' '}
                  (<strong>{t('manager.modules.newPickingLists.statusLabels.packed', {}, 'Pakket')}</strong>{t('manager.modules.newPickingLists.working.criteriaStatus50Suffix', {}, ' — pakket men ikke avsluttet')})
                </li>
              </ol>
              <p className="mt-1">
                {t('manager.modules.newPickingLists.working.criteriaWarehousePrefix', {}, 'Og minst én av plukklistens linjer tilhører ')}<strong>{t('manager.modules.newPickingLists.info.selectedWarehouse', {}, 'valgt lager')}</strong>.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <h3 className="font-bold text-gray-900 mb-1">{t('manager.modules.newPickingLists.info.cautionHeading', {}, 'Pass på')}</h3>
              <ul className="list-disc pl-5 space-y-0.5 text-gray-800">
                <li>
                  <strong>{t('manager.modules.newPickingLists.table.operator', {}, 'Operatør')}</strong>{t('manager.modules.newPickingLists.working.cautionEditableMiddle', {}, ' og ')}<strong>{t('manager.modules.newPickingLists.table.priority', {}, 'Prioritet')}</strong>{t('manager.modules.newPickingLists.working.cautionEditableSuffix', {}, ' kan redigeres inline ved å klikke i cellen')}
                </li>
                <li>
                  <strong>{t('manager.modules.newPickingLists.table.systemStatus', {}, 'Systemstatus')}</strong> (<code className="bg-yellow-100 px-1 rounded text-xs">LockStatus</code>){t('manager.modules.newPickingLists.working.cautionLockStatusMiddle', {}, ' viser om plukklisten er låst i bruk eller åpen. ')}<strong>{t('manager.modules.newPickingLists.table.lockedByCorrect', {}, 'Låst av')}</strong>{t('manager.modules.newPickingLists.working.cautionLockStatusSuffix', {}, ' viser hvilken operatør som har den åpen')}
                </li>
                <li>
                  <strong>{t('manager.modules.newPickingLists.working.deletionLabel', {}, 'Sletting')}</strong>{t('manager.modules.newPickingLists.working.cautionDeleteSuffix', {}, ' krever at: plukklisten har status ≤ 20, ikke er låst, og ingen linjer er plukket. Tilhørende ordrelinjer "frigjøres" og dukker opp i "Nye Ordre" igjen')}
                </li>
                <li>
                  {t('manager.modules.newPickingLists.working.cautionFinalizePrefix', {}, 'Når plukklisten avsluttes (status ')}<code className="bg-yellow-100 px-1 rounded text-xs">60</code>{t('manager.modules.newPickingLists.working.cautionFinalizeMiddle1', {}, ') eller plukk er nullet ut (tilbake til ')}<code className="bg-yellow-100 px-1 rounded text-xs">0</code>{t('manager.modules.newPickingLists.working.cautionFinalizeSuffix', {}, '), flyttes den ut av listen — til "Avsluttede plukklister" eller "Nye Plukklister"')}
                </li>
                <li>
                  {t('manager.modules.newPickingLists.info.statusTransitionsLabel', {}, 'Status-overganger:')}{' '}
                  <code className="bg-yellow-100 px-1 rounded text-xs">
                    0 (Ubehandlet) → 10 (Godkjent) → 20 (Under arbeid) → 40/45 (Plukket / Til pakking) → 50 (Pakket) → 60 (Avsluttet)
                  </code>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 px-4 py-2">
        <WarehouseTable
          data={tableData}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: true,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: true,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: true,
            stickyHeader: true,
            stickyFooter: true,
            compactMode: true,
            persistenceKey: 'working-picking-lists-table'
          }}
          summaryCalculations={summaryCalculations}
          getRowId={getRowId}
          selectedRows={selectedRowIds}
          onSelectionChange={handleSelectionChange}
          rowActions={rowActions}
          tableActions={tableActions}
          toolbarRight={
            <button
              onClick={() => setShowBulkModal(true)}
              disabled={selectedRowIds.length === 0}
              className="px-3 py-2 text-xs font-semibold text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              <ArrowUpDown className="w-4 h-4" />
              {selectedRowIds.length > 0
                ? t('manager.modules.newPickingLists.bulkUpdate.buttonWithCount', { '0': selectedRowIds.length }, `Oppdater valgte (${selectedRowIds.length})`)
                : t('manager.modules.newPickingLists.bulkUpdate.button', {}, 'Oppdater valgte')}
            </button>
          }
          expandableConfig={expandableConfig}
          loading={false}
          error={undefined}
          aria-label={t('manager.modules.newPickingLists.working.tableAriaLabel', {}, 'Plukklister under arbeid tabell')}
        />
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && pListToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg flex items-center space-x-3">
              <Trash2 size={24} />
              <h2 className="text-xl font-semibold">{t('manager.modules.newPickingLists.delete.confirmTitle', {}, 'Bekreft sletting')}</h2>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-4">
                {t('manager.modules.newPickingLists.delete.confirmQuestionPrefix', {}, 'Er du sikker på at du vil slette plukkliste ')}<strong>{pListToDelete.pListID}</strong>?
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-500">{t('manager.modules.newPickingLists.delete.descriptionLabel', {}, 'Beskrivelse:')}</div>
                  <div className="text-gray-900">{pListToDelete.description1 || '-'}</div>
                  <div className="text-gray-500">{t('manager.modules.newPickingLists.delete.numLinesLabel', {}, 'Antall linjer:')}</div>
                  <div className="text-gray-900">{pListToDelete.numOfOrderLines || 0}</div>
                  <div className="text-gray-500">{t('manager.modules.newPickingLists.delete.statusLabel', {}, 'Status:')}</div>
                  <div className="text-gray-900">{pListToDelete.statusText}</div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <strong>{t('manager.modules.newPickingLists.delete.warningLabel', {}, 'Advarsel:')}</strong> {t('manager.modules.newPickingLists.delete.warningBody', {}, 'Denne handlingen kan ikke angres. Plukklisten og alle tilhørende linjer vil bli permanent slettet.')}
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPListToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2"
              >
                <Trash2 size={16} />
                <span>{t('manager.modules.newPickingLists.actions.deletePickingList', {}, 'Slett plukkliste')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal (prioritet / leveringsdato / leveringsmåte) */}
      <BulkUpdateModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        selectedCount={selectedRowIds.length}
        isSaving={bulkSaving}
        progress={bulkProgress}
        deliveryMethods={deliveryMethods}
        onConfirm={handleBulkUpdate}
      />
    </div>
  );
};

export default WorkingPickingLists;
